/**
 * Asset URL utility for Azure Blob Storage
 * Generates URLs for assets stored in Azure, or falls back to the local
 * /assets path when REACT_APP_AZURE_STORAGE_URL is unset.
 *
 * REACT_APP_* values are inlined at build time by CRA, so changing the
 * variable in an environment requires a rebuild, not just a redeploy.
 */

/**
 * Get the full URL for an asset stored in Azure Blob Storage
 * @param {string} path - Path to asset (e.g., 'assets/images/hero.png')
 * @returns {string} Full URL to asset
 */
export const getAssetUrl = (path) => {
  const storageUrl = process.env.REACT_APP_AZURE_STORAGE_URL;

  // Development fallback: use local assets
  if (!storageUrl) {
    console.warn(
      '⚠️  REACT_APP_AZURE_STORAGE_URL not configured, using local assets. ' +
      'Set REACT_APP_AZURE_STORAGE_URL in .env.production for production'
    );
    return `/assets/${path.replace('assets/', '')}`;
  }

  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;

  // Remove trailing slash from storage URL if present
  // REACT_APP_AZURE_STORAGE_URL is the storage account base; append the assets container.
  const cleanUrl = (storageUrl.endsWith('/') ? storageUrl.slice(0, -1) : storageUrl) + '/website';

  return `${cleanUrl}/${cleanPath}`;
};

/**
 * Get optimized asset URL with responsive sizes
 * @param {string} filename - Filename (e.g., 'image_hero_desktop.png')
 * @param {number|string} width - Target width (320, 640, 1024, 1920, or 'original')
 * @returns {string} Full URL to optimized asset
 */
export const getOptimizedAssetUrl = (filename, width = 'original') => {
  const storageUrl = process.env.REACT_APP_AZURE_STORAGE_URL;

  if (!storageUrl) {
    return `/optimized-assets/${filename}`;
  }

  // REACT_APP_AZURE_STORAGE_URL is the storage account base; append the assets container.
  const cleanUrl = (storageUrl.endsWith('/') ? storageUrl.slice(0, -1) : storageUrl) + '/website';

  // Extract file extension
  const lastDot = filename.lastIndexOf('.');
  const ext = lastDot !== -1 ? filename.substring(lastDot + 1) : '';
  const baseName = lastDot !== -1 ? filename.substring(0, lastDot) : filename;

  if (width === 'original') {
    return `${cleanUrl}/optimized-assets/${baseName}-original.${ext}`;
  }

  // Return WebP version for specified width
  return `${cleanUrl}/optimized-assets/${baseName}-${width}w.webp`;
};

/**
 * Generate responsive image srcSet for picture/img elements
 * @param {string} filename - Base filename without size suffix
 * @param {Array<number>} sizes - Array of widths to generate (e.g., [320, 640, 1024, 1920])
 * @returns {string} srcSet string ready for use in img tag
 */
export const getResponsiveSrcSet = (filename, sizes = [320, 640, 1024, 1920]) => {
  return sizes
    .map(size => `${getOptimizedAssetUrl(filename, size)} ${size}w`)
    .join(', ');
};
